<template>
  <transition name="qp-toast">
    <div
      v-if="visible"
      :class="['qp-toast', `qp-toast--${toneClass}`]"
      role="status"
      aria-live="polite"
      @click="hide"
    >
      <span class="qp-toast__mark" aria-hidden="true">{{ toneMark }}</span>
      <span class="qp-toast__message">{{ message }}</span>
      <button class="qp-toast__close" type="button" aria-label="Dismiss" @click.stop="hide">
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1 1l8 8M9 1l-8 8" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </transition>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'ToastMessage',
  setup() {
    const visible = ref(false);
    const message = ref('');
    const type = ref('');
    let timer = null;

    const toneClass = computed(() => {
      const map = { success: 'success', error: 'error', warn: 'warning', warning: 'warning', info: 'info' };
      return map[type.value] || 'info';
    });

    const toneMark = computed(() => {
      const marks = { success: '✓', error: '×', warning: '!', info: 'i' };
      return marks[toneClass.value] || 'i';
    });

    const show = (msg, msgType = 'success') => {
      message.value = msg;
      type.value = msgType;
      visible.value = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { visible.value = false; }, 3500);
    };

    const hide = () => {
      visible.value = false;
      if (timer) { clearTimeout(timer); timer = null; }
    };

    return { visible, message, type, toneClass, toneMark, show, hide };
  },
};
</script>

<style scoped>
.qp-toast {
  position: fixed;
  bottom: var(--space-lg);
  right: var(--space-lg);
  z-index: var(--z-toast);
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  max-width: min(420px, calc(100vw - 2 * var(--space-lg)));
  padding: 10px var(--space-sm) 10px 10px;
  color: var(--ink);
  background: var(--surface-base);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.qp-toast__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  line-height: 1;
  border-radius: var(--radius-xs);
}

.qp-toast--success .qp-toast__mark { color: var(--success); background: var(--success-tint); }
.qp-toast--error   .qp-toast__mark { color: var(--danger);  background: var(--danger-tint); }
.qp-toast--warning .qp-toast__mark { color: var(--warning); background: var(--warning-tint); }
.qp-toast--info    .qp-toast__mark { color: var(--accent);  background: var(--accent-tint); }

.qp-toast__message {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
}

.qp-toast__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  color: var(--ink-faint);
  background: transparent;
  border: 0;
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: color var(--dur-xs) var(--ease-out-quart),
              background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-toast__close:hover {
  color: var(--ink);
  background: var(--surface-sunken);
}

/* Transitions */

.qp-toast-enter-from,
.qp-toast-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

.qp-toast-enter-active,
.qp-toast-leave-active {
  transition:
    opacity var(--dur-md) var(--ease-out-quart),
    transform var(--dur-md) var(--ease-out-expo);
}

@media (max-width: 560px) {
  .qp-toast {
    left: var(--space-sm);
    right: var(--space-sm);
    bottom: var(--space-sm);
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .qp-toast-enter-active,
  .qp-toast-leave-active { transition: opacity var(--dur-sm) linear; transform: none; }
}
</style>
